import * as React from "react";

import { cn } from "@/shared/lib/utils";

import { Checkbox } from "../primitives/checkbox";
import { Label } from "../primitives/label";

export interface ColocaCheckboxProps extends Omit<React.ComponentPropsWithoutRef<"div">, "onChange"> {
  checked: boolean;
  /**
   * Mixed state for a checkbox that stands for several others — the "select all" of a table page. Renders a
   * dash and reports `aria-checked="mixed"`; the next click resolves it to whatever `onCheckedChange`
   * decides.
   */
  indeterminate?: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Text to the right of the box. Clicking it toggles the checkbox. */
  label?: React.ReactNode;
  /** Helper line under the label — the consequence of checking, never a repeat of the label. */
  description?: React.ReactNode;
  /** Accessible name when there is no visible `label` — a checkbox alone in a table cell needs one. */
  "aria-label"?: string;
}

/**
 * Controlled checkbox outside a form — a table's row selector, a dialog's acknowledgment, a filter toggle.
 * Inside a `<ColocaForm>` use `ColocaCheckboxField`, which binds the same primitive to React Hook Form.
 *
 * @summary form-agnostic checkbox with label, description and indeterminate state
 */
export const ColocaCheckbox = React.forwardRef<HTMLDivElement, ColocaCheckboxProps>(function ColocaCheckbox(
  {
    checked,
    indeterminate = false,
    onCheckedChange,
    disabled = false,
    label,
    description,
    "aria-label": ariaLabel,
    id: idProp,
    className,
    ...rest
  },
  ref,
) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const hasText = label != null || description != null;

  return (
    <div
      ref={ref}
      className={cn("flex items-start gap-2", !hasText && "inline-flex", className)}
      data-disabled={disabled || undefined}
      {...rest}
    >
      <Checkbox
        id={id}
        className="mt-0.5"
        checked={checked}
        indeterminate={indeterminate}
        onCheckedChange={(next) => onCheckedChange(next)}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      {hasText && (
        <div className="grid gap-1 pt-0.5 leading-none">
          {label != null && (
            <Label htmlFor={id} className={cn(disabled && "cursor-not-allowed opacity-70")}>
              {label}
            </Label>
          )}
          {description != null && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      )}
    </div>
  );
});
